import { supabase } from '../lib/supabase';

export const employeeService = {
  async getAll() {
    const { data, error } = await supabase?.from('employees')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeNumber: row?.employee_number,
      firstName: row?.first_name,
      lastName: row?.last_name,
      email: row?.email,
      phone: row?.phone,
      dateOfBirth: row?.date_of_birth,
      employmentType: row?.employment_type,
      citizenStatus: row?.citizen_status,
      department: row?.department,
      position: row?.position,
      hireDate: row?.hire_date,
      terminationDate: row?.termination_date,
      isActive: row?.is_active,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getById(id) {
    const { data, error } = await supabase?.from('employees')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeNumber: data?.employee_number,
      firstName: data?.first_name,
      lastName: data?.last_name,
      email: data?.email,
      phone: data?.phone,
      dateOfBirth: data?.date_of_birth,
      employmentType: data?.employment_type,
      citizenStatus: data?.citizen_status,
      department: data?.department,
      position: data?.position,
      hireDate: data?.hire_date,
      terminationDate: data?.termination_date,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async create(employee) {
    const { data, error } = await supabase?.from('employees')?.insert({
        employee_number: employee?.employeeNumber,
        first_name: employee?.firstName,
        last_name: employee?.lastName,
        email: employee?.email,
        phone: employee?.phone,
        date_of_birth: employee?.dateOfBirth,
        employment_type: employee?.employmentType,
        citizen_status: employee?.citizenStatus,
        department: employee?.department,
        position: employee?.position,
        hire_date: employee?.hireDate
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeNumber: data?.employee_number,
      firstName: data?.first_name,
      lastName: data?.last_name,
      email: data?.email,
      phone: data?.phone,
      dateOfBirth: data?.date_of_birth,
      employmentType: data?.employment_type,
      citizenStatus: data?.citizen_status,
      department: data?.department,
      position: data?.position,
      hireDate: data?.hire_date,
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  async update(id, employee) {
    const { data, error } = await supabase?.from('employees')?.update({
        first_name: employee?.firstName,
        last_name: employee?.lastName,
        email: employee?.email,
        phone: employee?.phone,
        department: employee?.department,
        position: employee?.position,
        is_active: employee?.isActive
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeNumber: data?.employee_number,
      firstName: data?.first_name,
      lastName: data?.last_name,
      email: data?.email,
      phone: data?.phone,
      department: data?.department,
      position: data?.position,
      isActive: data?.is_active,
      updatedAt: data?.updated_at
    };
  },

  async delete(id) {
    const { error } = await supabase?.from('employees')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  async getByDepartment(department) {
    const { data, error } = await supabase?.from('employees')?.select('*')?.eq('department', department)?.eq('is_active', true);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeNumber: row?.employee_number,
      firstName: row?.first_name,
      lastName: row?.last_name,
      email: row?.email,
      department: row?.department,
      position: row?.position,
      employmentType: row?.employment_type,
      citizenStatus: row?.citizen_status
    })) || [];
  },

  async getNonCitizens() {
    const { data, error } = await supabase?.from('employees')?.select('*')?.eq('citizen_status', 'non_citizen')?.eq('is_active', true);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeNumber: row?.employee_number,
      firstName: row?.first_name,
      lastName: row?.last_name,
      email: row?.email,
      department: row?.department,
      position: row?.position,
      employmentType: row?.employment_type
    })) || [];
  },

  async validateEmployeeLimit(companyId, additionalEmployeeCount = 1) {
    try {
      // Get current subscription info
      const { data: subscription, error: subError } = await supabase
        ?.from('company_subscriptions')
        ?.select('current_tier, active_employee_count')
        ?.eq('company_id', companyId)
        ?.eq('is_active', true)
        ?.single();

      if (subError) throw subError;

      if (!subscription) {
        return {
          allowed: false,
          error: 'No active subscription found',
          currentCount: 0,
          maxAllowed: 0
        };
      }

      // Get tier limits
      const { data: tier, error: tierError } = await supabase
        ?.from('subscription_tiers')
        ?.select('tier_name, max_employees, monthly_price_kina')
        ?.eq('tier_name', subscription?.current_tier)
        ?.single();

      if (tierError) throw tierError;

      const currentCount = subscription?.active_employee_count || 0;
      const maxAllowed = tier?.max_employees;
      const newTotal = currentCount + additionalEmployeeCount;

      // Check if exceeds limit
      if (newTotal > maxAllowed) {
        // Get next tier recommendation
        const { data: nextTier, error: nextTierError } = await supabase
          ?.from('subscription_tiers')
          ?.select('tier_name, max_employees, monthly_price_kina')
          ?.gt('max_employees', maxAllowed)
          ?.order('max_employees', { ascending: true })
          ?.limit(1)
          ?.single();

        return {
          allowed: false,
          error: `Employee limit exceeded. Your current plan (${tier?.tier_name?.replace(/_/g, ' ')}) allows maximum ${maxAllowed} employees. You're trying to add ${additionalEmployeeCount} employee(s), which would bring your total to ${newTotal}.`,
          currentCount,
          maxAllowed,
          attemptedTotal: newTotal,
          currentTier: tier?.tier_name,
          currentTierPrice: tier?.monthly_price_kina,
          nextTier: nextTier ? {
            name: nextTier?.tier_name,
            maxEmployees: nextTier?.max_employees,
            price: nextTier?.monthly_price_kina
          } : null
        };
      }

      return {
        allowed: true,
        currentCount,
        maxAllowed,
        remainingSlots: maxAllowed - newTotal,
        newTotal
      };
    } catch (error) {
      throw new Error(error?.message || 'Failed to validate employee limit');
    }
  }
};

export default employeeService;